import { IPhysician } from "../../configuration/physician/models/physician.model";
import { ICommonlov } from "../../configuration/commonlov/models/commonlov.model";

export interface IProviderList extends IProviderDetail {
  networks: IProviderNetworks[];
  contractNetworkIds?: string[];
  contractNetworkDetails?: IProviderNetworks[];
  contractNetworks?: IProviderNetworks[];
}

interface IProviderNetworks {
  networkId: string;
  codeArabic?: string;
  codeEnglish?: string;
}

interface IAddress {
  addressId: string;
  countryId: string;
  stateId: string;
  cityId: string;
  countryNameEnglish?: string;
  countryNameArabic?: string;
  stateNameArabic?: string;
  stateNameEnglish?: string;
  cityNameArabic?: string;
  cityNameEnglish?: string;
  street: string;
  addressLine: string;
  zipcode: string;
  latitude: string;
  longitude: string;
}

interface IContact {
  contactId: string;
  telephone: string;
  mobileNumber: string;
  contactNumber: string;
  emergencyNumber: string;
  fax: string;
  email: string;
  website: string;
}

interface IBankAccount {
  bankAccountId: string;
  nameEnglish: string;
  nameArabic: string;
  accountNumber: string;
  bankAccountName: string;
  iban: string;
  countryNameEnglish?: string;
  countryNameArabic?: string;
  stateNameArabic?: string;
  stateNameEnglish?: string;
  cityNameArabic?: string;
  cityNameEnglish?: string;
  countryId: string;
  stateId: string;
  cityId: string;
}

interface IProviderDetail {
  providerId: string;
  contractProviderId?: string;
  nameEnglish: string;
  nameArabic: string;
  providerTypeId: string;
  providerTypeNameEnglish: string;
  providerTypeNameArabic: string;
  mohLicenseNumber: string;
  crNo: string;
  crexpiryDate: Date;
  vatRegistrationNumber: string;
  cchiId: string;
  cchiexpiryDate: Date;
  nphiesId: string;
  providerURL: string;
  address: IAddress;
  contact: IContact;
  bankAccount: IBankAccount;
  distanceAssessmentId: string;
  distanceAssessmentNameArabic?: string;
  distanceAssessmentNameEnglish?: string;
  similarProviderIds: string[];
  similarProviders: IProviderList[]
  providerGeneralLookId: string;
  providerGeneralLookNameArabic?: string;
  providerGeneralLookNameEnglish?: string;
  clinicIds: string[];
  clinics: ICommonlov[]
  medicalDeviceIds: string[];
  medicalDevices: ICommonlov[];
  hasRadiologicalProtectionCertificate: boolean;
  pharmacyAvailabilityId: string;
  pharmacyAvailabilityNameArabic?: string;
  pharmacyAvailabilityNameEnglish?: string;
  laboratoryAvailabilityId: string;
  laboratoryAvailabilityNameArabic?: string;
  laboratoryAvailabilityNameEnglish?: string;
  sterilizationAvailabilityId: string;
  sterilizationAvailabilityNameArabic?: string;
  sterilizationAvailabilityNameEnglish?: string;
  dentalLabAvailabilityId: string;
  dentalLabAvailabilityNameArabic?: string;
  dentalLabAvailabilityNameEnglish?: string;
  waitingRoomFurniture: ProviderWaitingRoomFurniture[];
  airConditioningTypeId: string;
  airConditioningTypeNameArabic?: string;
  airConditioningTypeNameEnglish?: string;
  airConditionerConditionId: string;
  airConditionerConditionNameArabic: string;
  airConditionerConditionNameEnglish?: string;
  hasUseOfSehaService: boolean;
  sehaServicesUsed: boolean;
  hasParkingLotAvailable: boolean;
  numberOfParkingSpaces: number;
  contacts: IProviderContact[];
  administrationContacts: ProviderAdministrationContact[];
  medicalContacts: ProviderMedicalContact[];
  erpProviderId?: string;
  createdDate?: string;
}

export interface ProviderMedicalContact {
  providerMedicalContactId?: string;
  physicianId?: string;
  physician?: IPhysician;
  consultantTypeId: string;
  consultantTypeNameEnglish?: string;
  consultantTypeNameArabic?: string;
  uniqueId: string;
}
export interface ProviderAdministrationContact {
  providerAdministrationContactId?: string;
  personDetailId: string;
  nameEnglish: string;
  nameArabic: string;
  providerStaffRoleId: string;
  providerStaffRoleNameEnglish?: string;
  providerStaffRoleNameArabic?: string;
  personalEmail?: string;
  personalMobile?: string;
  email: string;
  mobile: string;
  adminStaffAreaId: string;
  adminStaffAreaNameEnglish?: string;
  adminStaffAreaNameArabic?: string;
  uniqueId: string;
}
export interface IProviderContact {
  providerContactId?: string;
  personDetailId: string;
  nameEnglish: string;
  nameArabic: string;
  providerStaffRoleId: string;
  providerStaffRoleNameEnglish?: string;
  providerStaffRoleNameArabic?: string;
  personalEmail?: string;
  personalMobile?: string;
  email: string;
  mobile: string;
  uniqueId: string;
}

interface ProviderWaitingRoomFurniture {
  gender: string;
  furnitureIds: string[];
  furnitureDetails?: ICommonlov[];
}

interface ProviderAttachmentFile {
  file: File;
  ext: string;
  fileSize: number;
  isNotDeletable?: boolean;
}

export interface ProviderAttachmentReq {
  attachmentFile: ProviderAttachmentFile | null;
  supportingDocumentId: string;
  documentType: string;
  description?: string;
  providerAttachment: ProviderAttachment;
  isTouched?: boolean;
}

export interface ProviderAttachment {
  providerAttachmentId: string;
  supportingDocumentId: string;
  supportingDocumentNameEnglish: string;
  supportingDocumentNameArabic: string;
  supportingDocumentDescriptionEnglish: string;
  supportingDocumentDescriptionArabic: string;
  relativeFilePath: string;
  attachmentName: string;
  attachmentType: string;
  fileSize: number;
}

export const providerConfig = {
  entityName: 'PROVIDER.TITLE',
  nameEnglish: 'FORM_FIELD.ENGLISH_NAME',
  nameArabic: 'FORM_FIELD.ARABIC_NAME',
  crNo: 'FORM_FIELD.CR_NUMBER',
  mohLicenseNumber: 'FORM_FIELD.MOH_LICENSE_NUMBER',
  vatRegistrationNumber: 'FORM_FIELD.VAT_REGISTRATION_NUMBER',
  cchiId: 'FORM_FIELD.CCHI_ID',
  nphiesId: 'FORM_FIELD.NPHIES_ID',
  providerURL: 'FORM_FIELD.PROVIDER_URL',
  mobileNumber: 'FORM_FIELD.MOBILE_NO',
  telephone: 'FORM_FIELD.TELEPHONE',
  contactNumber: 'FORM_FIELD.CONTACT_NUMBER',
  emergencyNumber: 'FORM_FIELD.EMERGENCY_NUMBER',
  email: 'FORM_FIELD.EMAIL',
  website: 'FORM_FIELD.WEBSITE',
  fax: 'FORM_FIELD.FAX',
  providerTypeId: 'PROVIDER.TYPE',
  networkIds: 'CONFIGURATION.NETWORK.TITLE',
  street: 'FORM_FIELD.STREET',
  zipcode: 'FORM_FIELD.ZIPCODE',
  latitude: 'FORM_FIELD.LATITUDE',
  longitude: 'FORM_FIELD.LONGITUDE',
  countryId: 'COMMON.COUNTRY',
  stateId: 'COMMON.STATE',
  cityId: 'FORM_FIELD.CITY',
  bankAccountName: 'FORM_FIELD.CUSTOMER_NO',
  accountNumber: 'FORM_FIELD.ACCOUNT_NO',
  iban: 'FORM_FIELD.IBAN',
  crexpiryDate: 'FORM_FIELD.CR_EXPIRY_DATE',
  cchiexpiryDate: 'FORM_FIELD.CCHI_EXPIRY_DATE',
  ibanValidation: 'FORM_FIELD.IBAN_VALIDATION',
  zipCodeValidation: 'FORM_FIELD.ZIPCODE_VALIDATION',
  accountNumberValidation: 'FORM_FIELD.ACCOUNT_NUMBER_VALIDATION',
  personalMobile: 'PROVIDER.PERSONAL_MOBILE',
  personalEmail: 'PROVIDER.PERSONAL_EMAIL',
  name: 'FORM_FIELD.NAME',
  staffRole: 'PROVIDER.STAFF_ROLE',
  area: 'PROVIDER.AREA',
  consultant: 'PROVIDER.CONSULTANT',
  physician: 'CONFIGURATION.PHYSICIAN.TITLE',
  qualification: 'PROVIDER.QUALIFICATION',
  role: 'FORM_FIELD.ROLE',
  contactStaffInfo: 'FORM_FIELD.CONTACT_STAFF_INFO',
  adminStaffInfo: 'FORM_FIELD.ADMINSTRATION_STAFF_INFO',
  medicalStaffInfo: 'FORM_FIELD.MEDICAL_STAFF_INFO'
};

export type Info = 'contacts' | 'administrationContacts' | 'medicalContacts';

export const providerMedicalContactConfig = {
  entityName: 'FORM_FIELD.MEDICAL_STAFF_INFO',
};

export const providerAdministrationContactConfig = {
  entityName: 'FORM_FIELD.ADMINSTRATION_STAFF_INFO',
};